import { useEffect, useMemo, useState } from "react";
import { useQuery, useSubscription } from "@apollo/client";
import MyTimesheetFilterBody from "../filter-body";
import {
  useMyTimesheetFilterCounterState,
  useMyTimesheetFilterOpenState,
  useMyTimesheetFilterProperties,
} from "my-timesheet/hooks";
import {
  OcRoleFilterItemData,
  OcRoleFilterSubtypeCollapse,
} from "oc/components";
import {
  MY_TIMESHEET_FILTER_ENTITY_DEFINITONS,
  MY_TIMESHEET_FILTER_QUERIES,
  MY_TIMESHEET_FILTER_SUBSCRIPTIONS,
} from "oc/utils";

interface QueryParams {
  page: number;
  pageSize: number;
  sort: string;
  sortDirection: string;
  searchText: string;
  searchField: string;
}

export interface MyTimesheetFilterSubtypeProps {
  type: "project" | "cost" | "client";
}

type GraphQLRow = OcRoleFilterItemData & {
  color: string;
  createdAt: string;
  createdBy: string;
  description: string;
  isDeleted: boolean;
  modifiedAt: string | null;
  modifiedBy: string | null;
  validFrom: string;
  validTo: string | null;
  value?: number; // TODO f only cost has these.
  billable?: boolean; // TODO f only cost has these.
};

interface GraphQLInsideData {
  page: number;
  pageSize: number;
  rows: Omit<GraphQLRow, "code">[];
  total: number;
  totalPages: number;
  __typename: string;
}

type CleanGraphQLData = GraphQLInsideData & {
  rows: GraphQLRow[];
};

type ReturnEntityPossibleKeys =
  (typeof MY_TIMESHEET_FILTER_ENTITY_DEFINITONS)[keyof typeof MY_TIMESHEET_FILTER_ENTITY_DEFINITONS];

type GraphQLReturnData = Partial<
  Record<ReturnEntityPossibleKeys, GraphQLInsideData>
>;

export const MyTimesheetFilterSubtype = ({
  type,
}: MyTimesheetFilterSubtypeProps) => {
  const { state: openedFilter } = useMyTimesheetFilterOpenState();
  const { state: counter, setState: updateCounter } =
    useMyTimesheetFilterCounterState();

  const [data, setData] = useState<CleanGraphQLData | undefined>(undefined);

  const [filterProperties, setFilterProperties] = useState({
    page: 1,
    pageSize: 25,
    sort: "name",
    sortDirection: "asc",
    search: "",
  });

  const queryParams: QueryParams = useMemo(
    () => ({
      page: filterProperties.page,
      pageSize: filterProperties.pageSize,
      sort: filterProperties.sort,
      sortDirection: filterProperties.sortDirection,
      searchText: filterProperties.search,
      searchField: "name",
      validity: undefined,
    }),
    [filterProperties],
  );

  const { data: rawData, refetch } = useQuery<GraphQLReturnData>(
    MY_TIMESHEET_FILTER_QUERIES[type],
    {
      fetchPolicy: "cache-and-network",
      variables: queryParams,
    },
  );

  useSubscription(MY_TIMESHEET_FILTER_SUBSCRIPTIONS[type], {
    onData: () => {
      refetch();
    },
  });

  useEffect(() => {
    if (rawData) {
      const freshData = rawData[MY_TIMESHEET_FILTER_ENTITY_DEFINITONS[type]];
      if (freshData) {
        const rowsWithCodes: GraphQLRow[] = freshData.rows.map((row) => ({
          ...row,
          code: row._id,
        }));
        setData({
          ...freshData,
          rows: rowsWithCodes,
        });
      } else
        console.error(
          `Incorrect data! Requested type: ${type}, data got: ${rawData}`,
        );
    }
  }, [rawData, type]);

  const {
    availableItems,
    selectedItems,
    handleSearch,
    handleClearSearch,
    handleClickItem,
  } = useMyTimesheetFilterProperties({
    filterProperties,
    data,
    setFilterProperties,
    type: type,
  });

  useEffect(() => {
    const length = selectedItems?.length || 0;
    if (counter[type] !== length) {
      updateCounter({ ...counter, [type]: length });
    }
  }, [counter, selectedItems, updateCounter, type]);

  return (
    <MyTimesheetFilterBody open={openedFilter[type]} type={type} noBorder>
      <OcRoleFilterSubtypeCollapse
        opened
        availableItems={availableItems}
        selectedItems={selectedItems}
        dataTotal={data?.total}
        type={type}
        handleClickItem={handleClickItem}
        handleFetchMore={() => {}}
        onClearSearchText={handleClearSearch}
        onSearchTextChange={handleSearch}
        searchText={filterProperties.search}
        noCode
      />
    </MyTimesheetFilterBody>
  );
};
